import type { UseFormReturn } from 'react-hook-form';
import type { EditCryptoAddressFormValues } from './types';
import { useTranslation } from 'react-i18next';
import { useUpdateCryptoAddress, useDeleteCryptoAddress } from '../api';
import { useToast } from '@/shared/lib';
import { useCallback } from 'react';
import type { ConfirmationConfig } from '@/widgets';

type Props = {
  form: UseFormReturn<EditCryptoAddressFormValues>;
  templateId: string;
  onSuccess: () => void;
  openConfirmationModal: (config: ConfirmationConfig) => void;
};

type Params = {
  updateAddress: () => Promise<void>;
  deleteAddress: () => Promise<void>;
  isPending: boolean;
};

export const useEditCryptoAddressFormLogic = ({
  form,
  templateId,
  onSuccess,
  openConfirmationModal,
}: Props): Params => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const {
    mutateAsync: updateCryptoAddress,
    isPending: isUpdatingCryptoAddressPending,
  } = useUpdateCryptoAddress({
    onSuccess: () => {
      toast.success(t('savedAddresses.cryptoAddress.editing.result.success'));
      onSuccess();
    },
    onError: error => {
      toast.error(
        error.message || t('savedAddresses.cryptoAddress.editing.result.error')
      );
    },
  });

  const { mutateAsync: deleteCryptoAddress } = useDeleteCryptoAddress({
    onSuccess: () => {
      toast.success(t('savedAddresses.cryptoAddress.deletion.result.success'));
      onSuccess();
    },
    onError: error => {
      toast.error(
        error.message || t('savedAddresses.cryptoAddress.deletion.result.error')
      );
    },
  });

  const updateAddress = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    await updateCryptoAddress({
      networkName: form.getValues('networkName'),
      templateId,
      data: {
        templateName: form.getValues('templateName'),
      },
    });
  }, [form, templateId, updateCryptoAddress]);

  const deleteAddress = useCallback(async () => {
    openConfirmationModal({
      title: t('savedAddresses.cryptoAddress.deletion.title'),
      description: t('savedAddresses.cryptoAddress.deletion.description'),
      confirmText: t('savedAddresses.cryptoAddress.deletion.confirmText'),
      cancelText: t('savedAddresses.cryptoAddress.deletion.cancelText'),
      onConfirm: async () =>
        await deleteCryptoAddress({
          templateId,
          networkName: form.getValues('networkName'),
        }),
    });
  }, [form, templateId, deleteCryptoAddress]);

  return {
    updateAddress,
    deleteAddress,
    isPending: isUpdatingCryptoAddressPending,
  };
};
